import { ArrowUp, Loader2, Square } from 'lucide-react-native';
import { useEffect, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  InputAccessoryView,
  Keyboard,
  type NativeSyntheticEvent,
  Platform,
  Pressable,
  StyleSheet,
  type TextInput,
  type TextInputKeyPressEventData,
  View,
} from 'react-native';
import { Box, Text } from '../../../components/primitives';
import { FormInput } from '../../../components/primitives/FormInput';
import { colors } from '../../../theme/colors';
import { shadows } from '../../../theme/shadows';
import { tokens } from '../../../theme/tokens';
import { fieldPlaceholderColor } from '../../../theme/typography';

const CHAT_INPUT_ACCESSORY_ID = 'chat-input-accessory';

interface ChatInputProps {
  readonly onSend: (message: string) => void;
  readonly onCancel?: () => void;
  readonly isSearching?: boolean;
  readonly disabled?: boolean;
  readonly placeholder?: string;
  /**
   * Prefills the composer (edit-and-resend, FLOSEAI-553). Each non-null value
   * replaces the current text and focuses the input; clearing to null leaves
   * whatever the user has typed untouched.
   */
  readonly draft?: string | null;
}

export function ChatInput({
  onSend,
  onCancel,
  isSearching,
  disabled,
  placeholder = 'Search for companies by industry, size, location...',
  draft,
}: ChatInputProps) {
  const [text, setText] = useState('');
  const inputRef = useRef<TextInput>(null);
  const focusAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (draft != null) {
      setText(draft);
      inputRef.current?.focus();
    }
  }, [draft]);

  const canSend = text.trim().length > 0 && !disabled;
  const canCancel = Boolean(isSearching && onCancel);
  const canPressAction = canSend || canCancel;

  function handleSend() {
    if (!canSend && !canCancel) return;

    if (isSearching && onCancel) {
      onCancel();
      return;
    }

    const trimmed = text.trim();
    if (!trimmed) return;

    onSend(trimmed);
    setText('');
    inputRef.current?.blur();
    Keyboard.dismiss();
  }

  function handleKeyPress(event: NativeSyntheticEvent<TextInputKeyPressEventData>) {
    if (Platform.OS !== 'web') return;

    const nativeEvent = event.nativeEvent as TextInputKeyPressEventData & { shiftKey?: boolean };
    if (nativeEvent.key !== 'Enter') return;
    if (nativeEvent.shiftKey) return;

    (event as unknown as { preventDefault?: () => void }).preventDefault?.();
    handleSend();
  }

  function dismissKeyboard() {
    inputRef.current?.blur();
    Keyboard.dismiss();
  }

  function handleFocus() {
    Animated.timing(focusAnim, {
      toValue: 1,
      duration: 200,
      easing: Easing.out(Easing.ease),
      useNativeDriver: false,
    }).start();
  }

  function handleBlur() {
    Animated.timing(focusAnim, {
      toValue: 0,
      duration: 200,
      easing: Easing.out(Easing.ease),
      useNativeDriver: false,
    }).start();
  }

  const borderColor = focusAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [colors.border.subtle, colors.primary.main],
  });

  return (
    <Box
      paddingX="xl"
      paddingY="lg"
      borderTopWidth={1}
      borderTopColor={colors.border.subtle}
      backgroundColor={colors.background.primary}
    >
      <Animated.View
        style={[
          styles.container,
          { borderColor },
          Platform.OS === 'web'
            ? ({ transition: 'box-shadow 0.2s ease' } as Record<string, unknown>)
            : {},
        ]}
      >
        <FormInput
          ref={inputRef}
          style={styles.input}
          value={text}
          onChangeText={setText}
          placeholder={placeholder}
          placeholderTextColor={fieldPlaceholderColor}
          multiline
          maxLength={500}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onSubmitEditing={handleSend}
          onKeyPress={handleKeyPress}
          blurOnSubmit={false}
          inputAccessoryViewID={Platform.OS === 'ios' ? CHAT_INPUT_ACCESSORY_ID : undefined}
          editable={!disabled}
          accessibilityLabel="Chat message input"
          accessibilityHint="Type your message and press send"
        />

        <Pressable
          style={[
            styles.sendButton,
            canPressAction ? styles.sendButtonActive : styles.sendButtonDisabled,
          ]}
          onPress={handleSend}
          disabled={!canPressAction}
          accessibilityRole="button"
          accessibilityLabel={canCancel ? 'Cancel response' : 'Send chat message'}
        >
          {canCancel ? (
            <Square
              size={16}
              color={colors.text.primary}
              fill={colors.text.primary}
              strokeWidth={0}
            />
          ) : isSearching ? (
            <Loader2 size={16} color={colors.text.disabled} strokeWidth={2.25} />
          ) : (
            <ArrowUp
              size={18}
              color={canSend ? colors['primary-foreground'] : colors.text.disabled}
              strokeWidth={2.5}
            />
          )}
        </Pressable>
      </Animated.View>
      {Platform.OS === 'ios' && (
        <InputAccessoryView nativeID={CHAT_INPUT_ACCESSORY_ID}>
          <View style={styles.accessoryBar}>
            <Pressable
              onPress={dismissKeyboard}
              hitSlop={10}
              accessibilityRole="button"
              accessibilityLabel="Dismiss keyboard"
              style={styles.accessoryButton}
            >
              <Text variant="bodySmall" color={colors.primary.neon} style={styles.accessoryText}>
                Done
              </Text>
            </Pressable>
          </View>
        </InputAccessoryView>
      )}
    </Box>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    borderWidth: 1,
    borderRadius: tokens.radius.xl,
    backgroundColor: colors.background.input,
    paddingLeft: tokens.spacing.lg,
    paddingRight: 6,
    paddingVertical: 6,
    minHeight: 48,
    maxHeight: 120,
  },
  input: {
    flex: 1,
    fontSize: tokens.fontSize.basePlus,
    color: colors.text.primary,
    paddingVertical: Platform.OS === 'web' ? 8 : 6,
    maxHeight: 100,
    ...(Platform.OS === 'web' ? ({ outlineStyle: 'none' } as Record<string, string>) : {}),
  },
  sendButton: {
    width: 42,
    height: 42,
    borderRadius: tokens.radius.md,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 6,
  },
  sendButtonActive: {
    backgroundColor: colors.primary[500],
    ...(shadows.glowHalo as object),
  },
  sendButtonDisabled: {
    backgroundColor: colors.surface.row,
  },
  accessoryBar: {
    alignItems: 'flex-end',
    paddingHorizontal: 14,
    paddingVertical: 8,
    backgroundColor: colors.background.secondary,
    borderTopWidth: 1,
    borderTopColor: colors.border.subtle,
  },
  accessoryButton: {
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  accessoryText: {
    fontWeight: '700',
  },
});
